import {
    Flex,
    Box,
    Table,
    Checkbox,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    Tfoot,
    useColorModeValue,
} from '@chakra-ui/react';
import * as React from 'react';

import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';

// Custom components
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';

const columnHelper = createColumnHelper();

const parseNumber = (val) => {
    if (val == null) return 0;
    if (typeof val === 'number') return val;

    // keep digits, dot, comma and minus if ever present
    const raw = String(val).replace(/[^0-9,.\-]/g, '').trim();
    if (!raw) return 0;

    // normalize: remove thousands separators, use dot as decimal
    const normalized = raw.replace(/\./g, '').replace(',', '.');
    const n = parseFloat(normalized);
    return Number.isFinite(n) ? n : 0;
};

export default function CheckTable(props) {
    const { i18n } = useTranslation();
    const l = (en, ro) => (i18n.language === 'ro' ? ro : en);
    const { tableData = [] } = props;
    const textColor = useColorModeValue('secondaryGray.900', 'white');
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
    const summaryBg = useColorModeValue('gray.50', 'whiteAlpha.100');

    let defaultData = tableData;

    // --------- SUMMARY CALCULATION ----------
    const summary = React.useMemo(() => {
        let totalSalesSum = 0;
        let totalOrdersSum = 0;
        let totalUnitsSum = 0;
        const returnRates = [];

        for (const row of tableData) {
            totalSalesSum += parseNumber(row.totalSales);
            totalOrdersSum += parseNumber(row.quantity);
            totalUnitsSum += parseNumber(row.unitsSold);

            const rrNum = parseNumber(row.returnRate);
            if (!Number.isNaN(rrNum)) {
                returnRates.push(rrNum);
            }
        }

        let averageReturnRate = 0;

        const validReturnRates = returnRates.filter((x) => x > 0);

        if (validReturnRates.length > 0) {
            const sum = validReturnRates.reduce((acc, val) => acc + val, 0);
            averageReturnRate = sum / validReturnRates.length;
        }

        const formatInt = (n) => Math.round(n).toLocaleString('ro-RO');
        const formatMoney = (n) => `${Math.round(n).toLocaleString('ro-RO')} RON`;
        const formatPct = (n) => `${n.toFixed(2)}%`;

        return {
            totalSalesLabel: formatMoney(totalSalesSum),
            totalOrdersLabel: formatInt(totalOrdersSum),
            totalUnitsLabel: formatInt(totalUnitsSum),
            medianReturnRateLabel: formatPct(averageReturnRate),
        };
    }, [tableData]);

    // --------- COLUMNS ----------
    const columns = [
        columnHelper.accessor('name', {
            id: 'name',
            header: () => (
                <Text
                    justifyContent="space-between"
                    align="left"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                >
                    {l('NAME', 'NUME')}
                </Text>
            ),
            cell: (info) => (
                <Flex align="left">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                        {info.getValue()[0]}
                    </Text>
                </Flex>
            ),
        }),
        columnHelper.accessor('totalSales', {
            id: 'totalSales',
            header: () => (
                <Text
                    justifyContent="space-between"
                    align="left"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                >
                    {l('Total Sales', 'Vanzari totale')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize="sm" fontWeight="700">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('quantity', {
            id: 'quantity',
            header: () => (
                <Text
                    justifyContent="space-between"
                    align="left"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                >
                    {l('Total Orders', 'Comenzi totale')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize="sm" fontWeight="700">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('unitsSold', {
            id: 'unitsSold',
            header: () => (
                <Text
                    justifyContent="space-between"
                    align="left"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                >
                    {l('Total Units Sold', 'Unitati vandute')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize="sm" fontWeight="700">
                    {info.getValue()}
                </Text>
            ),
        }),
        columnHelper.accessor('returnRate', {
            id: 'returnRate',
            header: () => (
                <Text
                    justifyContent="space-between"
                    align="left"
                    fontSize={{ sm: '10px', lg: '12px' }}
                    color="gray.400"
                >
                    {l('Returned orders %', 'Comenzi returnate %')}
                </Text>
            ),
            cell: (info) => (
                <Text color={textColor} fontSize="sm" fontWeight="700">
                    {info.getValue()}
                </Text>
            ),
        }),
    ];

    const [data] = React.useState(() => [...defaultData]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        debugTable: true,
    });

    return (
        <Card flexDirection="column" w="100%" px="0px" overflowX={{ sm: 'scroll', lg: 'hidden' }}>
            <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
                <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
                    {l('Product Sales', 'Vanzari produse')}
                </Text>
            </Flex>
            <Box maxH="300px" overflowY="auto">
                <Table variant="simple" color="gray.500" mb="24px" mt="12px">
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Th
                                        key={header.id}
                                        colSpan={header.colSpan}
                                        pe="10px"
                                        borderColor={borderColor}
                                    >
                                        <Flex
                                            justifyContent="space-between"
                                            align="center"
                                            fontSize={{ sm: '10px', lg: '12px' }}
                                            color="gray.400"
                                        >
                                            {flexRender(header.column.columnDef.header, header.getContext())}
                                        </Flex>
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => (
                            <Tr key={row.id}>
                                {row.getVisibleCells().map((cell) => (
                                    <Td
                                        key={cell.id}
                                        fontSize={{ sm: '14px' }}
                                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                                        borderColor="transparent"
                                    >
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                    </Td>
                                ))}
                            </Tr>
                        ))}
                    </Tbody>

                    {/* SUMMARY ROW */}
                    <Tfoot>
                        <Tr bg={summaryBg}>
                            <Td
                                fontSize={{ sm: '14px' }}
                                fontWeight="800"
                                color={textColor}
                                borderTopWidth="1px"
                                borderColor={borderColor}
                            >
                                {l('Total', 'Total')}
                            </Td>
                            <Td
                                fontSize={{ sm: '14px' }}
                                fontWeight="700"
                                color={textColor}
                                borderTopWidth="1px"
                                borderColor={borderColor}
                            >
                                {summary.totalSalesLabel}
                            </Td>
                            <Td
                                fontSize={{ sm: '14px' }}
                                fontWeight="700"
                                color={textColor}
                                borderTopWidth="1px"
                                borderColor={borderColor}
                            >
                                {summary.totalOrdersLabel}
                            </Td>
                            <Td
                                fontSize={{ sm: '14px' }}
                                fontWeight="700"
                                color={textColor}
                                borderTopWidth="1px"
                                borderColor={borderColor}
                            >
                                {summary.totalUnitsLabel}
                            </Td>
                            <Td
                                fontSize={{ sm: '14px' }}
                                fontWeight="700"
                                color={textColor}
                                borderTopWidth="1px"
                                borderColor={borderColor}
                            >
                                {summary.medianReturnRateLabel}
                            </Td>
                        </Tr>
                    </Tfoot>
                </Table>
            </Box>
        </Card>
    );
}
